import { Theme } from "./types/core";
import { useFrameSettings } from "./hooks";
import { shadowRoot } from "./shadow";
import { useAppSelector } from "./store";
import { selectTheme } from "./actions/selectors";

// resolve #fff, #003394, 'white' and other css color names to 7 digit hex (e.g. #ffffff, #48234a)
function cssColorToExpandedHex(color: string): string {
  if (color.startsWith("#")) {
    if (color.length === 4) {
      color =
        "#" + color[1] + color[1] + color[2] + color[2] + color[3] + color[3];
    }
    return color;
  }

  // Create a temporary element to use the browser's color parsing
  const ctx = document.createElement("canvas").getContext("2d");
  if (!ctx) {
    throw new Error("Could not create canvas context");
  }

  ctx.fillStyle = color;
  return ctx.fillStyle;
}

const hexToRgb = (hex: string): [number, number, number] => {
  const cleanHex = hex.charAt(0) === "#" ? hex.substring(1) : hex;
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  return [r, g, b];
};

const rgbToLuminance = (r: number, g: number, b: number): number => {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

const rgbToHex = (r: number, g: number, b: number): string => {
  return (
    "#" +
    [r, g, b].map((x) => Math.round(x).toString(16).padStart(2, "0")).join("")
  );
};

export const computeHoverColor = (color: string): string => {
  const hex = cssColorToExpandedHex(color);
  const [r, g, b] = hexToRgb(hex);
  const luminance = rgbToLuminance(r, g, b);
  const isDark = luminance < 0.5;
  // For dark colors (low luminance), lighten
  // For light colors (high luminance), darken
  const adjustmentFactor = isDark ? 1.2 : 0.8;

  const newR = Math.min(255, r * adjustmentFactor);
  const newG = Math.min(255, g * adjustmentFactor);
  const newB = Math.min(255, b * adjustmentFactor);

  return rgbToHex(newR, newG, newB);
};

export const computeContrastColor = (color: string): string => {
  const hex = cssColorToExpandedHex(color);
  const [r, g, b] = hexToRgb(hex);
  const luminance = rgbToLuminance(r, g, b);
  const isLight = luminance > 0.3;
  return isLight ? "black" : "white";
};

export function applyTheme(theme: Theme, launcherColor: string) {
  // Find all elements with botbrains-theme class
  const elem = shadowRoot().querySelector(".botbrains-theme");
  if (elem == null) {
    throw new Error("[botbrains] No element with .botbrains-theme class found");
  }
  const root = elem as HTMLElement;
  root.style.setProperty("--theme-brand", theme.brand);
  root.style.setProperty("--theme-brand-hover", computeHoverColor(theme.brand));
  root.style.setProperty("--theme-header-bg", theme.header_bg);
  root.style.setProperty(
    "--theme-header-bg-hover",
    computeHoverColor(theme.header_bg)
  );
  root.style.setProperty(
    "--theme-header-close",
    computeContrastColor(theme.header_bg || "#000000")
  );
  root.style.setProperty("--theme-chat", theme.chat);
  root.style.setProperty("--theme-chat-text", theme.chat_text);
  root.style.setProperty("--theme-chat-text-light", theme.chat_text_light);
  root.style.setProperty("--theme-launcher", launcherColor);
}

export function getHexColorValue(id: "brand" | "brand-hover"): string {
  const elem = shadowRoot().querySelector(".botbrains-theme");
  if (elem == null) {
    throw new Error("[botbrains] No element with .botbrains-theme class found");
  }
  const root = elem as HTMLElement;
  return getComputedStyle(root).getPropertyValue(`--theme-${id}`).trim();
}

export function useTheme(frameId: string): void {
  const isDarkTheme = useAppSelector(selectTheme) === "dark";
  const { data: settings, status } = useFrameSettings(frameId);

  if (status === "succeeded") {
    let light = settings.themes.find((theme) => theme.name === "light");
    let dark = settings.themes.find((theme) => theme.name === "dark");
    if (light == undefined && dark == undefined) {
      throw new Error("Missing light or dark theme in settings");
    } else if (light == undefined) {
      console.debug("[botbrains]: Missing light theme in settings");
      light = dark;
    } else if (dark == undefined) {
      console.debug("[botbrains]: Missing dark theme in settings");
      dark = light;
    }
    const theme = isDarkTheme ? dark : light;
    const launcherColor = settings.launcher_settings.button_color;
    applyTheme(theme, launcherColor);
  }
}
